import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormControlLabel,
	Grid,
	IconButton,
	InputLabel,
	LinearProgress,
	MenuItem,
	Paper,
	Select,
	SelectChangeEvent,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TableSortLabel,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import EastIcon from '@mui/icons-material/East';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import AuthContext from '../../store/auth-context';
import SnackbarContext from '../../store/snackbar-context';
import { downloadBase64File } from '../../api/stage3-attestation-api';
import {
	convertTeacherLetterEnLabelsPdfToSheet,
	exportTeacherLetterCsv,
	exportTeacherLetterMergedPdf,
	exportTeacherLetterShippingPostXlsx,
	exportTeacherLetterSinglePdf,
	exportTeacherLetterZip,
	getTeacherLetterDetail,
	getTeacherLetterPreviewHtml,
	getTeacherLetterRows,
	getTeacherLetterSettings,
	saveTeacherLetterSettings,
	syncTeacherLetterTexts,
	TEACHER_LETTER_SYNC_BATCH_SIZE,
	TeacherLetterCohort,
	TeacherLetterRow,
	TeacherLetterSettings,
	updateTeacherLetterRow,
} from '../../api/teacher-letter-api';
import EnShippingDialog from './EnShippingDialog';

const TEACHER_LETTERS_INFO_TOOLTIP =
	'Listy gratulacyjne dla nauczycieli finalistów/laureatów — do wydruku, podpisu i wysyłki pocztą (bez skanów i maili). ' +
	'Kohorty: Wszyscy (uczniowie z tytułem) — Nauczyciele finalistów — Nauczyciele laureatów — Ręczna — Bez ZIP. ' +
	'Flaga „Bez ZIP” pomija nauczyciela w zbiorczym ZIP/PDF; wyjątki: Wszyscy, Ręczna, kohorta Bez ZIP. ' +
	'Zsynchronizuj teksty: partie po 25 (dopełniacze imion i nazwisk uczniów z Morfeusza — do korekty w edycji). ' +
	'Wiersze z uczniami z więcej niż jednej szkoły są wyróżnione — sprawdź akapit ręcznie.';

type TeacherLetterSortKey =
	| 'last_name'
	| 'district_name'
	| 'school_line'
	| 'students_with_title'
	| 'distinct_schools'
	| 'address_city';

const COHORTS: { id: TeacherLetterCohort; label: string }[] = [
	{ id: 'all', label: 'Wszyscy (nauczyciele uczniów z tytułem)' },
	{ id: 'finalist_teachers', label: 'Nauczyciele finalistów' },
	{ id: 'laureate_teachers', label: 'Nauczyciele laureatów' },
	{ id: 'manual', label: 'Ręczna (oznaczeni)' },
	{ id: 'no_zip_excluded', label: 'Bez ZIP' },
];

const TeacherLettersTab: React.FC = () => {
	const { token } = useContext(AuthContext);
	const { setMsg } = useContext(SnackbarContext);
	const [cohort, setCohort] = useState<TeacherLetterCohort>('all');
	const [settings, setSettings] = useState<TeacherLetterSettings | null>(null);
	const [rows, setRows] = useState<TeacherLetterRow[]>([]);
	const [loading, setLoading] = useState(false);
	const [busy, setBusy] = useState(false);
	const [searchQuery, setSearchQuery] = useState('');
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(25);
	const [previewIndex, setPreviewIndex] = useState(0);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);
	const [previewLoading, setPreviewLoading] = useState(false);
	const [previewRecordJump, setPreviewRecordJump] = useState('');
	const previewBlobUrlRef = useRef<string | null>(null);
	const [editOpen, setEditOpen] = useState(false);
	const [editRow, setEditRow] = useState<TeacherLetterRow | null>(null);
	const [editSaving, setEditSaving] = useState(false);
	/** Propozycje (akapit z morfologią) — po otwarciu dialogu, w tle. */
	const [editSuggestionsLoading, setEditSuggestionsLoading] = useState(false);
	const editSuggestionsRequestRef = useRef(0);
	const [sortKey, setSortKey] = useState<TeacherLetterSortKey>('last_name');
	const [sortAsc, setSortAsc] = useState(true);
	const [syncProgress, setSyncProgress] = useState<{ processed: number; total: number } | null>(null);
	const [syncDialogOpen, setSyncDialogOpen] = useState(false);
	const [syncFillMissingOnly, setSyncFillMissingOnly] = useState(false);
	const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
	const [shippingDialogOpen, setShippingDialogOpen] = useState(false);
	const [excludeSavingId, setExcludeSavingId] = useState<number | null>(null);
	const [manualSavingId, setManualSavingId] = useState<number | null>(null);

	const filteredRows = useMemo(() => {
		const q = searchQuery.trim().toLowerCase();
		if (!q) {
			return rows;
		}
		return rows.filter((r) => {
			const hay = [
				r.first_name,
				r.last_name,
				r.academic_degree,
				r.affiliation,
				r.school_line,
				r.district_name,
				r.address_city,
				r.email,
				...r.students.map((s) => `${s.first_name} ${s.last_name} ${s.school_name}`),
			]
				.join(' ')
				.toLowerCase();
			return hay.includes(q);
		});
	}, [rows, searchQuery]);

	const sortedFilteredRows = useMemo(() => {
		const dir = sortAsc ? 1 : -1;
		return [...filteredRows].sort((a, b) => {
			if (sortKey === 'students_with_title' || sortKey === 'distinct_schools') {
				const na = a[sortKey];
				const nb = b[sortKey];
				if (na !== nb) {
					return (na - nb) * dir;
				}
				return a.last_name.localeCompare(b.last_name, 'pl', { sensitivity: 'base' }) * dir;
			}
			const sa = String(a[sortKey] ?? '');
			const sb = String(b[sortKey] ?? '');
			return sa.localeCompare(sb, 'pl', { sensitivity: 'base' }) * dir;
		});
	}, [filteredRows, sortKey, sortAsc]);

	const handleSort = (key: TeacherLetterSortKey) => {
		if (sortKey === key) {
			setSortAsc((v) => !v);
		} else {
			setSortKey(key);
			setSortAsc(true);
		}
	};

	const previewCount = sortedFilteredRows.length;
	const displayPreviewIndex = previewCount > 0 ? Math.min(Math.max(0, previewIndex), previewCount - 1) : 0;

	useEffect(() => {
		if (previewCount > 0) {
			setPreviewRecordJump(String(displayPreviewIndex + 1));
		}
	}, [displayPreviewIndex, previewCount]);

	const commitPreviewRecordInput = useCallback(() => {
		if (previewCount === 0 || previewLoading) {
			return;
		}
		const n = parseInt(previewRecordJump.replace(/\s/g, ''), 10);
		if (!Number.isFinite(n)) {
			setPreviewRecordJump(String(displayPreviewIndex + 1));
			return;
		}
		const idx = Math.max(0, Math.min(previewCount - 1, n - 1));
		if (idx !== displayPreviewIndex) {
			setPreviewIndex(idx);
			setPage(Math.floor(idx / rowsPerPage));
		} else {
			setPreviewRecordJump(String(displayPreviewIndex + 1));
		}
	}, [previewCount, previewLoading, previewRecordJump, displayPreviewIndex, rowsPerPage]);

	const goToTeacherPreview = useCallback(
		(r: TeacherLetterRow) => {
			const idx = sortedFilteredRows.findIndex((x) => x.teacher_id === r.teacher_id);
			if (idx >= 0) {
				setPreviewIndex(idx);
				setPage(Math.floor(idx / rowsPerPage));
			}
		},
		[sortedFilteredRows, rowsPerPage],
	);

	const closeEditDialog = useCallback(() => {
		setEditOpen(false);
		setEditSuggestionsLoading(false);
		editSuggestionsRequestRef.current += 1;
	}, []);

	const openEdit = useCallback(
		(r: TeacherLetterRow) => {
			const req = ++editSuggestionsRequestRef.current;
			setEditRow(r);
			setEditOpen(true);
			setEditSuggestionsLoading(true);
			if (!token) {
				setEditSuggestionsLoading(false);
				return;
			}
			void (async () => {
				try {
					const res = await getTeacherLetterDetail(token, r.teacher_id);
					if (req !== editSuggestionsRequestRef.current) {
						return;
					}
					const d = res.data.teacher;
					if (!d) {
						return;
					}
					setEditRow((prev) => {
						if (!prev || prev.teacher_id !== d.teacher_id) {
							return prev;
						}
						return {
							...prev,
							salutation_suggested: d.salutation_suggested,
							addressee_name_line_suggested: d.addressee_name_line_suggested,
							school_line_suggested: d.school_line_suggested,
							achievement_paragraph_suggested: d.achievement_paragraph_suggested,
						};
					});
				} catch {
					if (req === editSuggestionsRequestRef.current) {
						setMsg({
							msg: 'Nie udało się pobrać propozycji (morfologia). Edycja działa — możesz spróbować ponownie.',
							severity: 'warning',
						});
					}
				} finally {
					if (req === editSuggestionsRequestRef.current) {
						setEditSuggestionsLoading(false);
					}
				}
			})();
		},
		[token, setMsg],
	);

	const loadSettings = useCallback(async () => {
		if (!token) {
			return;
		}
		const res = await getTeacherLetterSettings(token);
		setSettings(res.data);
	}, [token]);

	const loadTeachers = useCallback(async () => {
		if (!token) {
			return;
		}
		setLoading(true);
		try {
			const res = await getTeacherLetterRows(token, cohort);
			setRows(res.data.teachers ?? []);
		} catch {
			setMsg({ msg: 'Nie udało się wczytać listy nauczycieli', severity: 'error' });
		} finally {
			setLoading(false);
		}
	}, [token, cohort, setMsg]);

	useEffect(() => {
		loadSettings().catch(() => setMsg({ msg: 'Błąd wczytywania ustawień', severity: 'error' }));
	}, [loadSettings, setMsg]);

	useEffect(() => {
		loadTeachers();
	}, [loadTeachers]);

	useEffect(() => {
		setPreviewIndex(0);
		setPage(0);
	}, [cohort]);

	useEffect(() => {
		setPreviewIndex(0);
		setPage(0);
	}, [searchQuery]);

	useEffect(() => {
		setPreviewIndex(0);
	}, [sortKey, sortAsc]);

	useEffect(() => {
		if (previewCount > 0 && previewIndex > previewCount - 1) {
			setPreviewIndex(previewCount - 1);
		}
	}, [previewCount, previewIndex]);

	useEffect(() => {
		const maxPage = Math.max(0, Math.ceil(sortedFilteredRows.length / rowsPerPage) - 1);
		if (page > maxPage) {
			setPage(maxPage);
		}
	}, [sortedFilteredRows.length, rowsPerPage, page]);

	useEffect(() => {
		return () => {
			if (previewBlobUrlRef.current) {
				URL.revokeObjectURL(previewBlobUrlRef.current);
				previewBlobUrlRef.current = null;
			}
		};
	}, []);

	useEffect(() => {
		if (!token || previewCount === 0) {
			if (previewBlobUrlRef.current) {
				URL.revokeObjectURL(previewBlobUrlRef.current);
				previewBlobUrlRef.current = null;
			}
			setPreviewUrl(null);
			setPreviewLoading(false);
			return;
		}
		const teacherId = sortedFilteredRows[displayPreviewIndex].teacher_id;
		let cancelled = false;
		setPreviewLoading(true);
		getTeacherLetterPreviewHtml(token, teacherId)
			.then((res) => {
				if (cancelled) {
					return;
				}
				if (previewBlobUrlRef.current) {
					URL.revokeObjectURL(previewBlobUrlRef.current);
					previewBlobUrlRef.current = null;
				}
				const u = URL.createObjectURL(new Blob([res.data.html], { type: 'text/html;charset=utf-8' }));
				previewBlobUrlRef.current = u;
				setPreviewUrl(u);
			})
			.catch(() => {
				if (cancelled) {
					return;
				}
				if (previewBlobUrlRef.current) {
					URL.revokeObjectURL(previewBlobUrlRef.current);
					previewBlobUrlRef.current = null;
				}
				setPreviewUrl(null);
				setMsg({ msg: 'Nie udało się wczytać podglądu HTML', severity: 'error' });
			})
			.finally(() => {
				if (!cancelled) {
					setPreviewLoading(false);
				}
			});
		return () => {
			cancelled = true;
		};
	}, [token, sortedFilteredRows, displayPreviewIndex, previewCount, setMsg]);

	const onCohortChange = (e: SelectChangeEvent<TeacherLetterCohort>) => {
		setCohort(e.target.value as TeacherLetterCohort);
	};

	const onSaveSettings = async () => {
		if (!token || !settings) {
			return;
		}
		setBusy(true);
		try {
			const res = await saveTeacherLetterSettings(token, settings);
			setSettings(res.data);
			setMsg({ msg: 'Zapisano ustawienia', severity: 'success' });
		} catch {
			setMsg({ msg: 'Błąd zapisu ustawień', severity: 'error' });
		} finally {
			setBusy(false);
		}
	};

	const onSync = async () => {
		if (!token) {
			return;
		}
		setSyncDialogOpen(false);
		setBusy(true);
		setSyncProgress(null);
		let offset = 0;
		let totalUpdated = 0;
		const allErrors: { teacher_id: number; message: string }[] = [];
		try {
			while (true) {
				const res = await syncTeacherLetterTexts(
					token,
					cohort,
					offset,
					TEACHER_LETTER_SYNC_BATCH_SIZE,
					syncFillMissingOnly,
				);
				const data = res.data;
				if (data.error && data.message) {
					setMsg({ msg: `Synchronizacja: ${data.message}`, severity: 'error' });
					console.error('teacher-letters sync-texts', data);
					return;
				}
				totalUpdated += data.updated ?? 0;
				if (data.errors?.length) {
					allErrors.push(...data.errors);
				}
				const total = typeof data.total === 'number' ? data.total : 0;
				const processed =
					typeof data.processed_total === 'number'
						? data.processed_total
						: offset + (data.batch_processed ?? data.updated ?? 0);
				setSyncProgress({ processed, total });
				const finished = data.done === true || (total > 0 && processed >= total);
				if (finished) {
					break;
				}
				offset = typeof data.processed_total === 'number' ? data.processed_total : processed;
			}
			const errN = allErrors.length;
			if (errN > 0) {
				console.warn('Częściowe błędy sync (nauczyciel → komunikat):', allErrors);
				setMsg({
					msg: `Zsynchronizowano ${totalUpdated} nauczycieli; ${errN} wierszy z błędem (konsola).`,
					severity: 'warning',
				});
			} else {
				setMsg({ msg: `Zsynchronizowano teksty (${totalUpdated} nauczycieli, kohorta)`, severity: 'success' });
			}
			await loadTeachers();
		} catch (e: unknown) {
			const ax = e as { response?: { data?: { message?: string; error?: string } } };
			const m = ax.response?.data?.message || ax.response?.data?.error;
			setMsg({ msg: m ? `Błąd synchronizacji: ${m}` : 'Błąd synchronizacji (500)', severity: 'error' });
			console.error('teacher-letters sync-texts', e);
		} finally {
			setBusy(false);
			setSyncProgress(null);
		}
	};

	const onCsv = async () => {
		if (!token) {
			return;
		}
		setBusy(true);
		try {
			const res = await exportTeacherLetterCsv(token, cohort);
			downloadBase64File(res.data.file_base64, res.data.filename);
			setMsg({ msg: 'Pobrano CSV', severity: 'info' });
		} catch {
			setMsg({ msg: 'Błąd eksportu CSV', severity: 'error' });
		} finally {
			setBusy(false);
		}
	};

	const onZip = async () => {
		if (!token) {
			return;
		}
		setBusy(true);
		try {
			const res = await exportTeacherLetterZip(token, cohort);
			downloadBase64File(res.data.file_base64, res.data.filename);
			setMsg({ msg: 'Pobrano archiwum ZIP', severity: 'info' });
		} catch (e: unknown) {
			const err = e as { response?: { data?: { error?: string } } };
			setMsg({ msg: err.response?.data?.error || 'Błąd generowania ZIP', severity: 'error' });
		} finally {
			setBusy(false);
		}
	};

	const onMergedPdf = async () => {
		if (!token) {
			return;
		}
		setBusy(true);
		try {
			const res = await exportTeacherLetterMergedPdf(token, cohort);
			downloadBase64File(res.data.file_base64, res.data.filename, 'application/pdf');
			setMsg({ msg: 'Pobrano PDF zbiorczy', severity: 'info' });
		} catch (e: unknown) {
			const err = e as { response?: { data?: { error?: string } } };
			setMsg({ msg: err.response?.data?.error || 'Błąd generowania PDF zbiorczego', severity: 'error' });
		} finally {
			setBusy(false);
		}
	};

	const onSinglePdf = async () => {
		if (!token || previewCount === 0) {
			return;
		}
		const tid = sortedFilteredRows[displayPreviewIndex].teacher_id;
		setBusy(true);
		try {
			const res = await exportTeacherLetterSinglePdf(token, tid);
			downloadBase64File(res.data.file_base64, res.data.filename, 'application/pdf');
			setMsg({ msg: 'Pobrano PDF', severity: 'info' });
		} catch (e: unknown) {
			const err = e as { response?: { data?: { error?: string } } };
			setMsg({ msg: err.response?.data?.error || 'Błąd generowania PDF', severity: 'error' });
		} finally {
			setBusy(false);
		}
	};

	const toggleExcludeFromBulkExport = async (r: TeacherLetterRow, checked: boolean) => {
		if (!token) {
			return;
		}
		setExcludeSavingId(r.teacher_id);
		try {
			const res = await updateTeacherLetterRow(token, r.teacher_id, { exclude_from_bulk_export: checked });
			const updated = res.data.teacher;
			const tid = r.teacher_id;
			if (updated) {
				setRows((prev) => prev.map((row) => (row.teacher_id === tid ? updated : row)));
				setEditRow((cur) => (cur?.teacher_id === tid ? { ...cur, ...updated } : cur));
			} else {
				setRows((prev) =>
					prev.map((row) => (row.teacher_id === tid ? { ...row, exclude_from_bulk_export: checked } : row)),
				);
				setEditRow((cur) => (cur?.teacher_id === tid ? { ...cur, exclude_from_bulk_export: checked } : cur));
			}
		} catch {
			setMsg({ msg: 'Błąd zapisu wykluczenia', severity: 'error' });
		} finally {
			setExcludeSavingId(null);
		}
	};

	const toggleManualCohort = async (r: TeacherLetterRow, checked: boolean) => {
		if (!token) {
			return;
		}
		setManualSavingId(r.teacher_id);
		try {
			const res = await updateTeacherLetterRow(token, r.teacher_id, { manual_cohort: checked });
			const updated = res.data.teacher;
			const tid = r.teacher_id;
			if (updated) {
				setRows((prev) => prev.map((row) => (row.teacher_id === tid ? updated : row)));
				setEditRow((cur) => (cur?.teacher_id === tid ? { ...cur, ...updated } : cur));
			} else {
				setRows((prev) => prev.map((row) => (row.teacher_id === tid ? { ...row, manual_cohort: checked } : row)));
				setEditRow((cur) => (cur?.teacher_id === tid ? { ...cur, manual_cohort: checked } : cur));
			}
		} catch {
			setMsg({ msg: 'Błąd zapisu flagi kohorty ręcznej', severity: 'error' });
		} finally {
			setManualSavingId(null);
		}
	};

	/** Wstaw propozycję do edytowalnego pola (strzałka w dialogu edycji). */
	const insertSuggestion = (
		field: 'salutation' | 'addressee_name_line' | 'school_line' | 'achievement_paragraph',
		value?: string,
	) => {
		if (!editRow || !value?.trim()) {
			return;
		}
		setEditRow({ ...editRow, [field]: value.trim() });
	};

	const teacherAddressLine = (r: TeacherLetterRow) => {
		const street = [r.address_street, r.address_building].filter(Boolean).join(' ');
		const local = r.address_apartment ? `/${r.address_apartment}` : '';
		const cityPart = [r.address_zip, r.address_city].filter(Boolean).join(' ');
		return [street + local, cityPart].filter((x) => x.trim() !== '').join(', ');
	};

	return (
		<Box sx={{ maxWidth: 1400 }}>
			<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 2 }}>
				<Typography variant='h6'>Listy gratulacyjne — nauczyciele finalistów</Typography>
				<Tooltip title={TEACHER_LETTERS_INFO_TOOLTIP}>
					<IconButton
						size='small'
						aria-label='Informacje o kohortach i synchronizacji'
					>
						<InfoOutlinedIcon fontSize='small' />
					</IconButton>
				</Tooltip>
			</Box>

			<FormControl
				sx={{ minWidth: 420, mb: 2 }}
				size='small'
			>
				<InputLabel id='teacher-letter-cohort-label'>Kohorta</InputLabel>
				<Select<TeacherLetterCohort>
					labelId='teacher-letter-cohort-label'
					label='Kohorta'
					value={cohort}
					onChange={onCohortChange}
				>
					{COHORTS.map((c) => (
						<MenuItem
							key={c.id}
							value={c.id}
						>
							{c.label}
						</MenuItem>
					))}
				</Select>
			</FormControl>

			{syncProgress && (
				<Box sx={{ width: '100%', maxWidth: 560, mb: 1.5 }}>
					<Typography
						variant='body2'
						color='text.secondary'
						sx={{ mb: 0.5 }}
					>
						Synchronizacja tekstów: {syncProgress.processed}
						{syncProgress.total > 0 ? ` / ${syncProgress.total}` : '…'}
					</Typography>
					<LinearProgress
						variant={syncProgress.total > 0 ? 'determinate' : 'indeterminate'}
						value={syncProgress.total > 0 ? Math.min(100, (100 * syncProgress.processed) / syncProgress.total) : 0}
					/>
				</Box>
			)}
			<Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2 }}>
				<Button
					variant='contained'
					disabled={busy}
					onClick={() => setSyncDialogOpen(true)}
				>
					Zsynchronizuj teksty (kohorta)
				</Button>
				<Button
					variant='outlined'
					disabled={busy}
					onClick={() => setDownloadDialogOpen(true)}
				>
					Pobierz
				</Button>
				<Button
					variant='outlined'
					disabled={busy || !token}
					onClick={() => setShippingDialogOpen(true)}
				>
					Wysyłka
				</Button>
				{busy && <CircularProgress size={28} />}
			</Box>

			{settings && (
				<Accordion
					defaultExpanded={false}
					sx={{ maxWidth: 900, mb: 2 }}
				>
					<AccordionSummary expandIcon={<ExpandMoreIcon />}>
						<Typography variant='subtitle1'>Ustawienia treści (mPDF / szablon)</Typography>
					</AccordionSummary>
					<AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0 }}>
						<TextField
							label='Nagłówek komitetu (kilka linii, wyśrodkowany)'
							multiline
							minRows={3}
							value={settings.committee_header_block ?? ''}
							onChange={(e) => setSettings({ ...settings, committee_header_block: e.target.value })}
							fullWidth
						/>
						<FormControl
							size='small'
							sx={{ maxWidth: 420 }}
						>
							<InputLabel id='teacher-letter-logo-variant-label'>Logo nad nagłówkiem</InputLabel>
							<Select
								labelId='teacher-letter-logo-variant-label'
								label='Logo nad nagłówkiem'
								value={settings.logo_variant ?? (settings.show_header_logo ? 'standard' : 'none')}
								onChange={(e) => {
									const logoVariant = e.target.value as TeacherLetterSettings['logo_variant'];
									setSettings({
										...settings,
										logo_variant: logoVariant,
										show_header_logo: logoVariant !== 'none',
									});
								}}
							>
								<MenuItem value='none'>Brak logo</MenuItem>
								<MenuItem value='standard'>Standardowe logo olimpiady</MenuItem>
								<MenuItem value='letter'>Logo listu gratulacyjnego z assets</MenuItem>
							</Select>
						</FormControl>
						<TextField
							label='Linia miejsca i daty (prawa strona, np. Warszawa, dnia 12 czerwca 2026 r.)'
							value={settings.issue_line ?? ''}
							onChange={(e) => setSettings({ ...settings, issue_line: e.target.value })}
							fullWidth
						/>
						<TextField
							label='Tytuł dokumentu'
							value={settings.doc_title ?? ''}
							onChange={(e) => setSettings({ ...settings, doc_title: e.target.value })}
							sx={{ maxWidth: 420 }}
						/>
						<TextField
							label='Akapit stały nr 2 (po akapicie z uczniami)'
							multiline
							minRows={3}
							value={settings.body_paragraph_2 ?? ''}
							onChange={(e) => setSettings({ ...settings, body_paragraph_2: e.target.value })}
							helperText='Tokeny [forma żeńska/forma męska] są podmieniane wg Pani/Pan, np. „o [Pani/Pana] rozległej wiedzy”.'
							fullWidth
						/>
						<TextField
							label='Akapit stały nr 3 (gratulacje i życzenia)'
							multiline
							minRows={2}
							value={settings.body_paragraph_3 ?? ''}
							onChange={(e) => setSettings({ ...settings, body_paragraph_3: e.target.value })}
							fullWidth
						/>
						<TextField
							label='Funkcja podpisującego (nad polem na podpis)'
							multiline
							minRows={2}
							value={settings.signatory_title ?? ''}
							onChange={(e) => setSettings({ ...settings, signatory_title: e.target.value })}
							fullWidth
						/>
						<TextField
							label='Imię i nazwisko podpisującego (kursywa)'
							value={settings.signatory_name ?? ''}
							onChange={(e) => setSettings({ ...settings, signatory_name: e.target.value })}
							fullWidth
						/>
						<Typography
							variant='subtitle2'
							component='div'
							sx={{ fontWeight: 600 }}
						>
							Import XLS — Elektroniczny Nadawca (płatnik / nadawca)
						</Typography>
						<Typography
							variant='body2'
							color='text.secondary'
						>
							Opcjonalnie: te same dane w każdym wierszu w kolumnach Platnik* szablonu EN. Wartości startowe są
							kopiowane z ustawień Zaświadczeń III tej edycji.
						</Typography>
						<TextField
							label='Platnik (kod wg EN, opcjonalnie)'
							value={settings.en_xlsx_platnik_rodzaj ?? ''}
							onChange={(e) => setSettings({ ...settings, en_xlsx_platnik_rodzaj: e.target.value })}
							fullWidth
							size='small'
						/>
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
							<TextField
								label='Platnik — nazwa'
								value={settings.en_xlsx_platnik_nazwa ?? ''}
								onChange={(e) => setSettings({ ...settings, en_xlsx_platnik_nazwa: e.target.value })}
								sx={{ flex: '1 1 200px', minWidth: 180 }}
								size='small'
							/>
							<TextField
								label='Platnik — nazwa cd.'
								value={settings.en_xlsx_platnik_nazwa_cd ?? ''}
								onChange={(e) => setSettings({ ...settings, en_xlsx_platnik_nazwa_cd: e.target.value })}
								sx={{ flex: '1 1 200px', minWidth: 180 }}
								size='small'
							/>
						</Box>
						<TextField
							label='Platnik — ulica'
							value={settings.en_xlsx_platnik_ulica ?? ''}
							onChange={(e) => setSettings({ ...settings, en_xlsx_platnik_ulica: e.target.value })}
							fullWidth
							size='small'
						/>
						<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
							<TextField
								label='Platnik — nr domu'
								value={settings.en_xlsx_platnik_dom ?? ''}
								onChange={(e) => setSettings({ ...settings, en_xlsx_platnik_dom: e.target.value })}
								sx={{ flex: '1 1 120px', minWidth: 100 }}
								size='small'
							/>
							<TextField
								label='Platnik — nr lokalu'
								value={settings.en_xlsx_platnik_lokal ?? ''}
								onChange={(e) => setSettings({ ...settings, en_xlsx_platnik_lokal: e.target.value })}
								sx={{ flex: '1 1 120px', minWidth: 100 }}
								size='small'
							/>
							<TextField
								label='Platnik — kod pocztowy'
								value={settings.en_xlsx_platnik_kod ?? ''}
								onChange={(e) => setSettings({ ...settings, en_xlsx_platnik_kod: e.target.value })}
								sx={{ flex: '1 1 140px', minWidth: 120 }}
								size='small'
							/>
							<TextField
								label='Platnik — miejscowość'
								value={settings.en_xlsx_platnik_miejscowosc ?? ''}
								onChange={(e) => setSettings({ ...settings, en_xlsx_platnik_miejscowosc: e.target.value })}
								sx={{ flex: '1 1 180px', minWidth: 160 }}
								size='small'
							/>
						</Box>
						<TextField
							label='Platnik — NIP (opcjonalnie)'
							value={settings.en_xlsx_platnik_nip ?? ''}
							onChange={(e) => setSettings({ ...settings, en_xlsx_platnik_nip: e.target.value })}
							sx={{ maxWidth: 280 }}
							size='small'
						/>
						<Typography
							variant='body2'
							color='text.secondary'
						>
							Konwersja PDF etykiet EN → arkusz A4 (105×57 mm): przesunięcie siatki w dół (+) / w górę (−), mm (−20…40);
							dodatkowy odstęp między rzędami etykiet (0…10 mm).
						</Typography>
						<TextField
							label='Przesunięcie siatki etykiet (mm)'
							type='number'
							inputProps={{ min: -20, max: 40, step: 0.5 }}
							value={settings.en_label_sheet_offset_top_mm ?? 0}
							onChange={(e) => {
								const v = Math.max(-20, Math.min(40, Number(e.target.value) || 0));
								setSettings({ ...settings, en_label_sheet_offset_top_mm: v });
							}}
							sx={{ maxWidth: 280 }}
							helperText='Wartości dodatnie przesuwają całość w dół.'
						/>
						<TextField
							label='Dodatkowy odstęp między rzędami (mm)'
							type='number'
							inputProps={{ min: 0, max: 10, step: 0.5 }}
							value={settings.en_label_sheet_row_gap_extra_mm ?? 0}
							onChange={(e) => {
								const v = Math.max(0, Math.min(10, Number(e.target.value) || 0));
								setSettings({ ...settings, en_label_sheet_row_gap_extra_mm: v });
							}}
							sx={{ maxWidth: 280 }}
						/>
						<Button
							variant='contained'
							disabled={busy}
							onClick={onSaveSettings}
							sx={{ alignSelf: 'flex-start' }}
						>
							Zapisz ustawienia
						</Button>
					</AccordionDetails>
				</Accordion>
			)}

			<Divider sx={{ my: 2 }} />
			<Typography
				variant='subtitle1'
				sx={{ mb: 1 }}
			>
				Podgląd dokumentu (HTML)
			</Typography>
			<TextField
				size='small'
				label='Szukaj (nauczyciel, stopień, szkoła, uczeń, okręg, miasto, e-mail)'
				value={searchQuery}
				onChange={(e) => setSearchQuery(e.target.value)}
				sx={{ mb: 1, maxWidth: 560 }}
				fullWidth
			/>
			<Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
				<Button
					size='small'
					variant='outlined'
					aria-label='Pierwszy rekord'
					disabled={previewCount === 0 || displayPreviewIndex <= 0 || previewLoading}
					onClick={() => {
						setPreviewIndex(0);
						setPage(0);
					}}
					sx={{ minWidth: 40, px: 0.75 }}
				>
					{'<<'}
				</Button>
				<Button
					size='small'
					variant='outlined'
					aria-label='Poprzedni rekord'
					disabled={previewCount === 0 || displayPreviewIndex <= 0 || previewLoading}
					onClick={() => setPreviewIndex((i) => Math.max(0, i - 1))}
					sx={{ minWidth: 36, px: 0.75 }}
				>
					{'<'}
				</Button>
				<Tooltip title='Numer rekordu na aktualnej liście (wyszukiwanie i sort). Enter lub opuszczenie pola — przejście.'>
					<TextField
						size='small'
						label='Nr'
						value={previewRecordJump}
						onChange={(e) => setPreviewRecordJump(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								e.preventDefault();
								commitPreviewRecordInput();
							}
						}}
						onBlur={() => {
							commitPreviewRecordInput();
						}}
						sx={{ width: 56 }}
						disabled={previewCount === 0 || previewLoading}
						inputProps={{ inputMode: 'numeric', 'aria-label': 'Numer rekordu podglądu' }}
					/>
				</Tooltip>
				{previewCount > 0 && sortedFilteredRows[displayPreviewIndex] && (
					<Typography
						variant='body2'
						component='span'
						sx={{
							minWidth: 0,
							maxWidth: { xs: '100%', sm: 420 },
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							whiteSpace: 'nowrap',
						}}
					>
						{' '}
						z {previewCount} - {sortedFilteredRows[displayPreviewIndex].last_name}{' '}
						{sortedFilteredRows[displayPreviewIndex].first_name}
					</Typography>
				)}
				{previewCount === 0 && (
					<Typography
						variant='body2'
						color='text.secondary'
						component='span'
					>
						brak rekordów
					</Typography>
				)}
				<Button
					size='small'
					variant='outlined'
					aria-label='Następny rekord'
					disabled={previewCount === 0 || displayPreviewIndex >= previewCount - 1 || previewLoading}
					onClick={() => setPreviewIndex((i) => Math.min(previewCount - 1, i + 1))}
					sx={{ minWidth: 36, px: 0.75 }}
				>
					{'>'}
				</Button>
				<Button
					size='small'
					variant='outlined'
					aria-label='Ostatni rekord'
					disabled={previewCount === 0 || displayPreviewIndex >= previewCount - 1 || previewLoading}
					onClick={() => {
						const last = previewCount - 1;
						setPreviewIndex(last);
						setPage(Math.floor(last / rowsPerPage));
					}}
					sx={{ minWidth: 40, px: 0.75 }}
				>
					{'>>'}
				</Button>
				<Button
					size='small'
					variant='outlined'
					disabled={previewCount === 0 || previewLoading || !sortedFilteredRows[displayPreviewIndex]}
					onClick={() => {
						const r = sortedFilteredRows[displayPreviewIndex];
						if (r) {
							openEdit(r);
						}
					}}
				>
					Edytuj ten rekord
				</Button>
				<Button
					size='small'
					variant='contained'
					disabled={busy || previewCount === 0 || previewLoading}
					onClick={() => void onSinglePdf()}
				>
					Pobierz PDF
				</Button>
				{previewLoading && <CircularProgress size={22} />}
			</Box>
			<Box
				sx={{
					border: 1,
					borderColor: 'divider',
					borderRadius: 1,
					height: { xs: 780, md: 1170 },
					mb: 3,
					bgcolor: 'grey.100',
					overflow: 'hidden',
					width: '100%',
					maxWidth: { xs: '100%', md: '65%' },
					mx: 'auto',
				}}
			>
				{previewUrl ? (
					<iframe
						title='Podgląd listu gratulacyjnego HTML'
						src={previewUrl}
						style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
					/>
				) : (
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							height: '100%',
							p: 2,
						}}
					>
						<Typography
							variant='body2'
							color='text.secondary'
						>
							{loading
								? 'Wczytywanie listy…'
								: previewCount === 0
									? 'Brak nauczycieli w tej kohortzie — wybierz inną lub zsynchronizuj teksty.'
									: previewLoading
										? 'Generowanie podglądu…'
										: 'Brak podglądu.'}
						</Typography>
					</Box>
				)}
			</Box>

			<Typography
				variant='subtitle1'
				sx={{ mb: 1 }}
			>
				Podgląd danych
				{searchQuery.trim() ? ` (${filteredRows.length} z ${rows.length})` : ` (${rows.length})`}
			</Typography>
			<TableContainer
				component={Paper}
				sx={{ maxHeight: 682, overflow: 'auto' }}
			>
				<Table
					size='small'
					stickyHeader
				>
					<TableHead>
						<TableRow>
							<TableCell sx={{ width: 44, px: 0.5 }}> </TableCell>
							<TableCell
								align='center'
								sx={{ maxWidth: 52, px: 0.25 }}
							>
								<Tooltip title='Zaznacz, aby pominąć tego nauczyciela w ZIP i w jednym PDF zbiorczym (np. list już wręczony).'>
									<span>Bez ZIP</span>
								</Tooltip>
							</TableCell>
							<TableCell
								align='center'
								sx={{ maxWidth: 52, px: 0.25 }}
							>
								<Tooltip title='Kohorta „Ręczna (oznaczeni)” — własna lista do osobnego procesu.'>
									<span>Ręczna</span>
								</Tooltip>
							</TableCell>
							<TableCell sx={{ maxWidth: 140, px: 0.5 }}>
								<TableSortLabel
									active={sortKey === 'last_name'}
									direction={sortKey === 'last_name' && !sortAsc ? 'desc' : 'asc'}
									onClick={() => handleSort('last_name')}
								>
									Nauczyciel / stopień
								</TableSortLabel>
							</TableCell>
							<TableCell sx={{ maxWidth: 72, px: 0.5 }}>
								<TableSortLabel
									active={sortKey === 'district_name'}
									direction={sortKey === 'district_name' && !sortAsc ? 'desc' : 'asc'}
									onClick={() => handleSort('district_name')}
								>
									Okręg
								</TableSortLabel>
							</TableCell>
							<TableCell sx={{ maxWidth: 220, px: 0.5 }}>
								<TableSortLabel
									active={sortKey === 'school_line'}
									direction={sortKey === 'school_line' && !sortAsc ? 'desc' : 'asc'}
									onClick={() => handleSort('school_line')}
								>
									Szkoła (adresat)
								</TableSortLabel>
							</TableCell>
							<TableCell sx={{ maxWidth: 240, px: 0.5 }}>
								<TableSortLabel
									active={sortKey === 'students_with_title'}
									direction={sortKey === 'students_with_title' && !sortAsc ? 'desc' : 'asc'}
									onClick={() => handleSort('students_with_title')}
								>
									Uczniowie z tytułem
								</TableSortLabel>
							</TableCell>
							<TableCell
								align='center'
								sx={{ maxWidth: 70, px: 0.5 }}
							>
								<TableSortLabel
									active={sortKey === 'distinct_schools'}
									direction={sortKey === 'distinct_schools' && !sortAsc ? 'desc' : 'asc'}
									onClick={() => handleSort('distinct_schools')}
								>
									<Tooltip title='Liczba różnych szkół uczniów — przy >1 sprawdź akapit ręcznie (wyróżniony wiersz).'>
										<span>Szkoły</span>
									</Tooltip>
								</TableSortLabel>
							</TableCell>
							<TableCell sx={{ maxWidth: 180, px: 0.5 }}>
								<TableSortLabel
									active={sortKey === 'address_city'}
									direction={sortKey === 'address_city' && !sortAsc ? 'desc' : 'asc'}
									onClick={() => handleSort('address_city')}
								>
									Adres wysyłki
								</TableSortLabel>
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{loading ? (
							<TableRow>
								<TableCell colSpan={9}>Wczytywanie…</TableCell>
							</TableRow>
						) : filteredRows.length === 0 ? (
							<TableRow>
								<TableCell
									colSpan={9}
									sx={{ py: 3 }}
								>
									{rows.length === 0
										? 'Brak nauczycieli w kohortcie.'
										: 'Brak wyników dla wpisanego tekstu — wyczyść pole wyszukiwania.'}
								</TableCell>
							</TableRow>
						) : (
							sortedFilteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((r) => (
								<TableRow
									key={r.teacher_id}
									hover
									sx={{
										cursor: 'pointer',
										...(r.distinct_schools > 1 ? { bgcolor: 'warning.50' } : {}),
									}}
									onClick={() => goToTeacherPreview(r)}
								>
									<TableCell
										sx={{ py: 0.5, px: 0.5 }}
										onClick={(e) => e.stopPropagation()}
									>
										<Tooltip title='Edytuj'>
											<IconButton
												size='small'
												aria-label='Edytuj'
												disabled={loading}
												onClick={() => openEdit(r)}
											>
												<EditOutlinedIcon fontSize='small' />
											</IconButton>
										</Tooltip>
									</TableCell>
									<TableCell
										align='center'
										sx={{ py: 0.25, px: 0.25 }}
										onClick={(e) => e.stopPropagation()}
									>
										<Checkbox
											size='small'
											checked={!!r.exclude_from_bulk_export}
											disabled={excludeSavingId === r.teacher_id || loading}
											onChange={(_, checked) => void toggleExcludeFromBulkExport(r, checked)}
											inputProps={{ 'aria-label': 'Wyklucz z eksportu zbiorczego' }}
										/>
									</TableCell>
									<TableCell
										align='center'
										sx={{ py: 0.25, px: 0.25 }}
										onClick={(e) => e.stopPropagation()}
									>
										<Checkbox
											size='small'
											checked={!!r.manual_cohort}
											disabled={manualSavingId === r.teacher_id || loading}
											onChange={(_, checked) => void toggleManualCohort(r, checked)}
											inputProps={{ 'aria-label': 'Kohorta ręczna' }}
										/>
									</TableCell>
									<TableCell sx={{ maxWidth: 160, verticalAlign: 'top' }}>
										<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
											<Typography
												component='span'
												variant='body2'
												sx={{ fontWeight: 600, lineHeight: 1.25 }}
											>
												{r.last_name}
											</Typography>
											<Typography
												component='span'
												variant='body2'
												color='text.secondary'
												sx={{ lineHeight: 1.25 }}
											>
												{r.first_name}
											</Typography>
											{r.academic_degree && (
												<Typography
													variant='caption'
													color='text.secondary'
													sx={{ lineHeight: 1.25 }}
												>
													{r.academic_degree}
												</Typography>
											)}
										</Box>
									</TableCell>
									<TableCell>{r.district_name}</TableCell>
									<TableCell sx={{ maxWidth: 260, whiteSpace: 'normal', verticalAlign: 'top' }}>
										{r.school_line || r.affiliation || '—'}
									</TableCell>
									<TableCell sx={{ maxWidth: 280, whiteSpace: 'normal', verticalAlign: 'top' }}>
										<Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
											{r.students.map((s) => (
												<Typography
													key={s.student_id}
													variant='caption'
													sx={{ lineHeight: 1.35, display: 'block' }}
												>
													{s.last_name} {s.first_name}
													{s.final_title ? ` — ${s.final_title}` : ''}
												</Typography>
											))}
										</Box>
									</TableCell>
									<TableCell
										align='center'
										sx={{ verticalAlign: 'top' }}
									>
										<Typography
											variant='body2'
											sx={{ fontWeight: r.distinct_schools > 1 ? 700 : 400 }}
											color={r.distinct_schools > 1 ? 'warning.main' : 'text.primary'}
										>
											{r.distinct_schools}
										</Typography>
									</TableCell>
									<TableCell sx={{ maxWidth: 200, whiteSpace: 'normal', verticalAlign: 'top' }}>
										{teacherAddressLine(r) || '—'}
									</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</TableContainer>
			<TablePagination
				component='div'
				rowsPerPageOptions={[10, 25, 50, 100]}
				count={sortedFilteredRows.length}
				rowsPerPage={rowsPerPage}
				page={page}
				onPageChange={(_, p) => setPage(p)}
				onRowsPerPageChange={(e) => {
					setRowsPerPage(parseInt(e.target.value, 10));
					setPage(0);
				}}
				labelRowsPerPage='Wierszy na stronę'
				labelDisplayedRows={({ from, to, count }) => `${from}–${to} z ${count}`}
			/>

			<EnShippingDialog
				open={shippingDialogOpen}
				onClose={() => setShippingDialogOpen(false)}
				disabled={!token}
				step1Description={
					'Najpierw wygeneruj i zaimportuj przesyłki w EN. Przesyłka polecona krajowa — adres korespondencyjny ' +
					'nauczyciela z rejestracji. numerPrzesylkiKlienta: OLiJP lub OLiJP-SP, „_LG_edycja”, numer rzymski edycji ' +
					'olimpiady, id nauczyciela (np. OLiJP_LG_edycjaVII_45). Masa musi być zgodna z FORMAT_GABARYT_EPO: ' +
					'S — max 500 g, M — max 1000 g, L — max 2000 g. Dopiero po imporcie pobierz z EN PDF z etykietami ' +
					'i użyj kroku 2 poniżej.'
				}
				step2Description={
					'PDF z Elektronicznego Nadawcy (jedna etykieta na stronę, np. ~101×54 mm) można ułożyć na arkuszu A4 ' +
					'w siatce 2×5 (10 etykiet na stronę). Układ siatki i odstępy można dopasować w ustawieniach treści tej ' +
					'zakładki (akordeon powyżej). Skalowanie zachowuje proporcje (dopasowanie do komórki 105×57 mm).'
				}
				exportFile={(params) => exportTeacherLetterShippingPostXlsx(token, cohort, params)}
				convertLabelsPdf={(fileBase64) => convertTeacherLetterEnLabelsPdfToSheet(token, fileBase64)}
			/>

			<Dialog
				open={editOpen && editRow !== null}
				onClose={() => !editSaving && closeEditDialog()}
				fullWidth
				maxWidth='lg'
				PaperProps={{ sx: { width: 'min(1100px, 98vw)', maxHeight: '92vh' } }}
			>
				<DialogTitle>Edycja tekstów listu gratulacyjnego</DialogTitle>
				<DialogContent
					dividers
					sx={{ pt: 2 }}
				>
					{editRow && (
						<>
							<Typography
								variant='body2'
								component='div'
								sx={{ fontSize: '1.125rem', fontWeight: 700, mb: 2 }}
							>
								{editRow.last_name} {editRow.first_name} (ID {editRow.teacher_id})
							</Typography>
							<Grid
								container
								spacing={2}
							>
								<Grid
									item
									xs={12}
									md={6}
								>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: 1,
											mb: 1,
											minHeight: 24,
										}}
									>
										<Typography
											variant='overline'
											color='text.secondary'
											component='span'
											sx={{ lineHeight: 1.2 }}
										>
											Źródło: baza / automat (podgląd)
										</Typography>
										<Box
											sx={{
												width: 22,
												height: 22,
												flexShrink: 0,
												display: 'flex',
												alignItems: 'center',
												justifyContent: 'center',
											}}
										>
											{editSuggestionsLoading && (
												<CircularProgress
													size={18}
													thickness={5}
													aria-label='Wczytywanie propozycji z morfologii'
												/>
											)}
										</Box>
									</Box>
									<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
										<Box>
											<Typography
												variant='caption'
												color='text.secondary'
												display='block'
											>
												Dane z rejestracji nauczyciela
											</Typography>
											<Typography
												variant='body2'
												sx={{ p: 1, borderRadius: 1, bgcolor: 'action.hover', whiteSpace: 'pre-wrap' }}
											>
												{[
													`${editRow.personal_title || '—'} ${editRow.first_name} ${editRow.last_name}`,
													editRow.academic_degree ? `Stopień: ${editRow.academic_degree}` : 'Stopień: —',
													editRow.affiliation ? `Afiliacja: ${editRow.affiliation}` : 'Afiliacja: —',
													`Adres: ${teacherAddressLine(editRow) || '—'}`,
												].join('\n')}
											</Typography>
										</Box>
										<Box>
											<Typography
												variant='caption'
												color='text.secondary'
												display='block'
											>
												Uczniowie z tytułem ({editRow.students_with_title}; szkół: {editRow.distinct_schools})
											</Typography>
											<Typography
												variant='body2'
												sx={{ p: 1, borderRadius: 1, bgcolor: 'action.hover', whiteSpace: 'pre-wrap' }}
											>
												{editRow.students.length > 0
													? editRow.students
															.map(
																(s) =>
																	`${s.first_name} ${s.last_name} — ${s.final_title || 'bez tytułu'}` +
																	(s.class_roman ? `, klasa ${s.class_roman}` : '') +
																	(s.school_name ? `, ${s.school_name}` : ''),
															)
															.join('\n')
													: '—'}
											</Typography>
											{editRow.distinct_schools > 1 && (
												<Typography
													variant='caption'
													color='warning.main'
													display='block'
													sx={{ mt: 0.5 }}
												>
													Uczniowie z więcej niż jednej szkoły — sprawdź akapit i ewentualnie pogrupuj ręcznie.
												</Typography>
											)}
										</Box>
										<Divider />
										<Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
											<Box sx={{ flex: 1, minWidth: 0 }}>
												<Typography
													variant='caption'
													color='text.secondary'
													display='block'
													sx={{ mb: 0.25 }}
												>
													Propozycja: zwrot grzecznościowy / linia nazwiska
												</Typography>
												<Typography
													variant='body2'
													sx={{
														p: 0.75,
														borderRadius: 1,
														bgcolor: 'action.hover',
														whiteSpace: 'pre-wrap',
														wordBreak: 'break-word',
													}}
												>
													{[editRow.salutation_suggested, editRow.addressee_name_line_suggested]
														.filter((x) => x?.trim())
														.join('\n') || '—'}
												</Typography>
											</Box>
											<Tooltip title='Wstaw propozycje do pól „Zwrot grzecznościowy” i „Linia z nazwiskiem”'>
												<span>
													<IconButton
														size='small'
														aria-label='Wstaw propozycję zwrotu i nazwiska'
														disabled={
															!editRow.salutation_suggested?.trim() && !editRow.addressee_name_line_suggested?.trim()
														}
														onClick={() => {
															setEditRow({
																...editRow,
																...(editRow.salutation_suggested?.trim()
																	? { salutation: editRow.salutation_suggested.trim() }
																	: {}),
																...(editRow.addressee_name_line_suggested?.trim()
																	? { addressee_name_line: editRow.addressee_name_line_suggested.trim() }
																	: {}),
															});
														}}
														sx={{ mb: 0.25 }}
													>
														<EastIcon sx={{ fontSize: 18 }} />
													</IconButton>
												</span>
											</Tooltip>
										</Box>
										<Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
											<Box sx={{ flex: 1, minWidth: 0 }}>
												<Typography
													variant='caption'
													color='text.secondary'
													display='block'
													sx={{ mb: 0.25 }}
												>
													Propozycja linii szkoły (afiliacja / szkoła ucznia)
												</Typography>
												<Typography
													variant='body2'
													sx={{
														p: 0.75,
														borderRadius: 1,
														bgcolor: 'action.hover',
														whiteSpace: 'pre-wrap',
														wordBreak: 'break-word',
													}}
												>
													{editRow.school_line_suggested?.trim() ? editRow.school_line_suggested : '—'}
												</Typography>
											</Box>
											<Tooltip title='Wstaw propozycję do pola „Szkoła / afiliacja”'>
												<span>
													<IconButton
														size='small'
														aria-label='Wstaw propozycję linii szkoły'
														disabled={!editRow.school_line_suggested?.trim()}
														onClick={() => insertSuggestion('school_line', editRow.school_line_suggested)}
														sx={{ mb: 0.25 }}
													>
														<EastIcon sx={{ fontSize: 18 }} />
													</IconButton>
												</span>
											</Tooltip>
										</Box>
										<Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5 }}>
											<Box sx={{ flex: 1, minWidth: 0 }}>
												<Typography
													variant='caption'
													color='text.secondary'
													display='block'
													sx={{ mb: 0.25 }}
												>
													Propozycja akapitu osiągnięć (odmiany z Morfeusza — sprawdź dopełniacze nazwisk)
												</Typography>
												<Typography
													variant='body2'
													sx={{
														p: 0.75,
														borderRadius: 1,
														bgcolor: 'action.hover',
														whiteSpace: 'pre-wrap',
														wordBreak: 'break-word',
													}}
												>
													{editRow.achievement_paragraph_suggested?.trim()
														? editRow.achievement_paragraph_suggested
														: editSuggestionsLoading
															? 'Generowanie propozycji…'
															: '—'}
												</Typography>
											</Box>
											<Tooltip title='Wstaw propozycję do pola „Akapit osiągnięć”'>
												<span>
													<IconButton
														size='small'
														aria-label='Wstaw propozycję akapitu'
														disabled={!editRow.achievement_paragraph_suggested?.trim()}
														onClick={() =>
															insertSuggestion('achievement_paragraph', editRow.achievement_paragraph_suggested)
														}
														sx={{ mb: 0.25 }}
													>
														<EastIcon sx={{ fontSize: 18 }} />
													</IconButton>
												</span>
											</Tooltip>
										</Box>
									</Box>
								</Grid>
								<Grid
									item
									xs={12}
									md={6}
								>
									<Typography
										variant='overline'
										color='text.secondary'
										display='block'
										sx={{ mb: 1 }}
									>
										W liście (edytowalne)
									</Typography>
									<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
										<TextField
											label='Zwrot grzecznościowy (np. Szanowna Pani)'
											value={editRow.salutation}
											onChange={(e) => setEditRow({ ...editRow, salutation: e.target.value })}
											fullWidth
											size='small'
										/>
										<TextField
											label='Linia z nazwiskiem (np. mgr Marzena Jarzyna)'
											value={editRow.addressee_name_line}
											onChange={(e) => setEditRow({ ...editRow, addressee_name_line: e.target.value })}
											fullWidth
											size='small'
										/>
										<TextField
											label='Szkoła / afiliacja (pod nazwiskiem)'
											multiline
											minRows={2}
											maxRows={6}
											value={editRow.school_line}
											onChange={(e) => setEditRow({ ...editRow, school_line: e.target.value })}
											helperText='Enter = nowa linia w PDF/HTML.'
											fullWidth
										/>
										<TextField
											label='Akapit osiągnięć (cała treść z uczniami)'
											multiline
											minRows={6}
											maxRows={16}
											value={editRow.achievement_paragraph}
											onChange={(e) => setEditRow({ ...editRow, achievement_paragraph: e.target.value })}
											helperText='Pełny akapit „Pragniemy przekazać…” — tu poprawiaj odmiany nazwisk, liczbę mnogą i ewentualne grupowanie szkół.'
											fullWidth
										/>
									</Box>
								</Grid>
							</Grid>
						</>
					)}
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => closeEditDialog()}
						disabled={editSaving}
					>
						Anuluj
					</Button>
					<Button
						variant='contained'
						disabled={editSaving || !editRow}
						onClick={async () => {
							if (!token || !editRow) {
								return;
							}
							setEditSaving(true);
							try {
								const res = await updateTeacherLetterRow(token, editRow.teacher_id, {
									salutation: editRow.salutation,
									addressee_name_line: editRow.addressee_name_line,
									school_line: editRow.school_line,
									achievement_paragraph: editRow.achievement_paragraph,
								});
								if (res.data.teacher) {
									setEditRow(res.data.teacher);
								}
								setMsg({ msg: 'Zapisano', severity: 'success' });
								closeEditDialog();
								await loadTeachers();
							} catch {
								setMsg({ msg: 'Błąd zapisu', severity: 'error' });
							} finally {
								setEditSaving(false);
							}
						}}
					>
						Zapisz
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={downloadDialogOpen}
				onClose={() => !busy && setDownloadDialogOpen(false)}
				fullWidth
				maxWidth='sm'
			>
				<DialogTitle>Pobierz</DialogTitle>
				<DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
					<Typography
						variant='body2'
						color='text.secondary'
					>
						Bieżąca kohorta: {COHORTS.find((c) => c.id === cohort)?.label ?? cohort}. Listy są generowane bez numeracji
						— do wydruku i podpisu; nauczyciele z flagą „Bez ZIP” są pomijani w plikach zbiorczych (wyjątki: Wszyscy,
						Ręczna, kohorta Bez ZIP).
					</Typography>
					<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
						<Button
							fullWidth
							variant='outlined'
							disabled={busy}
							onClick={() => void onCsv()}
						>
							Pobierz CSV (dane i teksty)
						</Button>
						<Button
							fullWidth
							variant='outlined'
							disabled={busy}
							onClick={() => void onZip()}
						>
							ZIP — PDF-y listów (po jednym pliku)
						</Button>
						<Button
							fullWidth
							variant='outlined'
							disabled={busy}
							onClick={() => void onMergedPdf()}
						>
							PDF zbiorczy — wszystkie listy w jednym pliku
						</Button>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setDownloadDialogOpen(false)}
						disabled={busy}
					>
						Zamknij
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={syncDialogOpen}
				onClose={() => !busy && setSyncDialogOpen(false)}
				fullWidth
				maxWidth='sm'
			>
				<DialogTitle>Synchronizacja tekstów</DialogTitle>
				<DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
					<Typography
						variant='body2'
						color='text.secondary'
					>
						Dla każdego nauczyciela w kohortcie zostaną przeliczone z bazy: zwrot grzecznościowy, linia z nazwiskiem
						(stopień z rejestracji), linia szkoły (afiliacja, w razie braku szkoła pierwszego ucznia) oraz akapit
						osiągnięć z listą uczniów (dopełniacze imion i nazwisk z Morfeusza — wymagają sprawdzenia).
					</Typography>
					<FormControlLabel
						control={
							<Checkbox
								checked={syncFillMissingOnly}
								onChange={(e) => setSyncFillMissingOnly(e.target.checked)}
							/>
						}
						label='Tylko uzupełnij puste pola (nie nadpisuj już wypełnionych / poprawionych tekstów)'
					/>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setSyncDialogOpen(false)}
						disabled={busy}
					>
						Anuluj
					</Button>
					<Button
						variant='contained'
						disabled={busy}
						onClick={() => void onSync()}
					>
						Kontynuuj
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default TeacherLettersTab;
